import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import $ from 'jquery';
import {
	Segment,
	compareNumbers,
} from './util';

import { onlyUnique } from 'lib/util/array';
import { removeHashParameterFromUrl } from 'lib/util/url';
import { getUniqueIdOfElement } from 'lib/util/helpers';

import { Family, WarrantyExtension, WarrantyData, Product, FamilyData, VariantData, Overrides } from './Product';
import Details, { FeatureGroups } from './Details';
import { compareSubscriptions, SubscriptionValueListItem, SIMPLE_PAY_DUMMY_SUBSCRIPTION } from './Subscription';

import deparam from 'lib/vendor/jquery-deparam-patched';

declare global {
	interface HTMLAnchorElement {
		suffix : string;
		selectors : string;
		pathNameOnly : string;
	}
}

export interface ListenerModule extends Module {
	productDataChannel? : string;
	onInvalidateProductData?(data : ProductStatus) : void;
	updateProductDataStatus?(info : ExposedProductInfo, origin? : any) : void;
	requestProductData?(info? : RequestProductInfo) : void;
	messageProductDataHub?(message : string, data : any) : void;
}
/**
 * Streamlines the process of subscribing a module to a product data hub.
 *
 * Writes the following properties to the module:
 *  • messageProductDataHub: Method for sending arbitrary messages on the hub’s channel.
 *  • requestProductData: A method that tells the hub to send new data to the callback
 *  • updateProductDataStatus: A method that sets the updated status on the hub
 *  • onInvalidateProductData: Internal handler for channel messages.
 *                             Not to be used directly. Overridden if already existing.
 *
 * @param {(function|string)} cb the callback/method name which is called with the data
 */
export function simpleSubscribe(
	module : Module,
	callback : ((info : ExposedProductInfo, origin? : any) => void) | string,
) {
	const mod = module as ListenerModule;
	const cb : (info : ExposedProductInfo, origin? : any) => void = $.proxy(
		typeof callback === 'string' ?
		(mod as any)[callback] :
		// tslint:disable-next-line:no-unbound-method
		(callback || mod.onInvalidateProductData || $.noop),
		mod,
	);
	mod.sandbox.subscribe(INITIAL_CHANNEL_NAME, mod);

	// Add proxy requestProductData method
	mod.requestProductData = $.noop;

	// Add proxy updateProductDataStatus method which sets flag to update status ASAP
	let updateImmediate : false | IArguments = false;
	mod.updateProductDataStatus = function updateProductDataStatus() {
		updateImmediate = arguments;
	};

	// Fire the subscribe message on the initial channel.
	mod.fire('subscribeForProductData', {
		element: mod.$ctx,
		subscribe(channel : string, productDataAlreadyLoaded : boolean) {
			// The EcommerceProductDataHub that feels itself responsible for the given module…
			// …will answer and send us the name of its channel, to which we will then subscribe.
			mod.sandbox.unsubscribe(INITIAL_CHANNEL_NAME, mod);

			function messageProductDataHub(message : string, data : any) {
				mod.fire(message, data, [channel]);
			}

			mod.messageProductDataHub = messageProductDataHub;

			// Main handler for product info (initial and on status changes)
			// tslint:disable-next-line:no-unbound-method
			const existingOnInvalidateProductData = mod.onInvalidateProductData;
			mod.onInvalidateProductData = function onInvalidateProductData(data : ProductStatus) {
				cb(data.info as ExposedProductInfo, data.origin);
				if(existingOnInvalidateProductData) {
					existingOnInvalidateProductData.call(this, data);
				}
			};
			if (mod.productDataChannel) {
				mod.sandbox.unsubscribe(mod.productDataChannel, mod);
			}
			mod.productDataChannel = channel;

			// Subscribe to the given channel
			mod.sandbox.subscribe(mod.productDataChannel, mod);

			// Manual refresh trigger
			function requestProductData(status? : RequestProductInfo) {
				messageProductDataHub('getInfo', {
					callback: cb,
					status,
				});
			}

			mod.requestProductData = requestProductData;

			// Status update trigger
			function updateProductDataStatus(status : ProductInfo, origin? : any) {
				origin = origin || getUniqueIdOfElement(mod.$ctx);
				messageProductDataHub('updateStatus', {origin, status});
			}

			mod.updateProductDataStatus = updateProductDataStatus;
			// Trigger immediate status update
			if (updateImmediate) {
				mod.updateProductDataStatus.apply(mod, updateImmediate as any as [ExposedProductInfo, any?]);
			}

			// If component was lazy loaded, request initialization data from data hub.
			if (productDataAlreadyLoaded && !updateImmediate) {
				requestProductData();
			}
		},
	}, [INITIAL_CHANNEL_NAME]);
}


export interface ValueListItem {
	value : string;
	text : string;
}
/**
 * Helper function to remove duplicates from key-text object list as used with (dropdown) filters.
 *
 * @method removeDuplicates
 * @param {Object} list
 * @return {Array} an array of {value, …} objects.
 */
function removeDuplicates(list : ValueListItem[]) {
	const map : {[key : string] : ValueListItem} = {};

	return list.map(function(item) {
		map[item.value] = item;

		return item.value;
	}).filter(onlyUnique).map(function(value) {
		return map[value];
	});
}

interface WarrantyMap {
	[productId : string] : string;
}

interface WarrantyInfo {
	__mapping : WarrantyMap;
	[warrantyId : string] : WarrantyMap | WarrantyExtension;
}

/**
 * Map and reduce warranties object
 *
 * @method mapVariantsToWarranties
 * @return {Object} warranties
 */
function mapVariantsToWarranties(warrantyInfo : WarrantyInfo) : WarrantyData {
	const mapping = warrantyInfo.__mapping || {};
	const result : WarrantyData = {};

	Object.keys(mapping).forEach(key => {
		const warrantyId = mapping[key];
		if(warrantyId in warrantyInfo) {
			const warranty = warrantyInfo[warrantyId] as WarrantyExtension;
			result[key] = {price: warranty.price, guaranteePeriod: warranty.guaranteePeriod};
		}
	});

	return result;
}

// Initial channel where all dependent modules register their subscriptions
export const INITIAL_CHANNEL_NAME = 'EcommerceProductDataHub';
const INTERACTIVE_CONTAINER_CHANNEL_NAME = 'BCInteractiveMode';

/**
 * List of possible status properties
 */
const STATUS_PROPERTIES = ['subscriptionId', 'subscriptionDuration', 'subscriptionType', 'color', 'memory'];

/**
 * List of possible subscription types
 */
export const enum SUBSCRIPTION_TYPES {
	createNew = 'new',
	retention = 'retention',
	without = 'without',
}

const PREFIX_FILTER_NAME = 'filter-';

const w = window;

export interface ProductInfo {
	memory : string | null;
	color : string | null;
	quantity : number;
	dimension : string | number | null;
	productId : string;
	subscriptionId : string | null;
	subscriptionDuration : string | number | null;
	subscriptionType : SUBSCRIPTION_TYPES | null;
	deepLink? : string | false;
	url? : string | false;
}

export interface ExposedProductInfo extends ProductInfo {
	product? : Product;
	family : Family;
}

export type RequestProductInfo = {
	[s in keyof ProductInfo]? : ProductInfo[s]
};

export interface ProductStatus {
	info : ProductInfo | ExposedProductInfo;
	origin? : string;
}

interface ProductInfoDefaults {
	freetext : string;
	memory? : string;
	color? : string;
	dimension? : string;
	productId? : string;
	subscriptionId? : string | string[];
	subscriptionDuration : string | number;
	subscriptionType? : SUBSCRIPTION_TYPES;
	data? : ExposedProductInfo;
}

export interface Texts {
	[key : string] : string;
}

export interface Filters {
	[filterName : string] : ValueListItem[];
}

export interface FilterStates {
	disableValues : string[];
	hideValues : string[];
}

export interface FilterStateRequest {
	filterName : keyof Filters;
	callback(filterStates : FilterStates) : void;
}

interface InfoWithProduct {
	product : Product;
}

interface AvailabilityForFilter {
	filter : string;
	exists : boolean;
	isAvailable? : boolean;
}

/**
 * Subscribes to channels: [EcommerceProductDataHub, BCInteractiveMode]
 * Listens to channel events: [SubscribeForProductData, UpdateStatus, GetFilters, GetInfo, InteractiveStateChanged]
 * Triggers channel events:
 *     startProductDataSubscription,
 *     invalidateProductData,
 *     disableMemorySizes,
 *     interactiveStateChanged
 *
 * @namespace Tc.Module
 * @class EcommerceProductDataHub
 * @extends Tc.Module
 */
export default class EcommerceProductDataHub extends Module {
	public static simpleSubscribe = simpleSubscribe;
	private segment! : Segment;
	private productFamily! : Family;
	private channel! : string;
	private isEditMode = $(document.body).hasClass('cq_isEditMode');
	private isMobile = $(document.body).hasClass('is-mobile');
	private isResponsibleForPage = false;
	private supportsHistoryApi = false;
	private defaults! : ProductInfoDefaults;
	private currentStatus! : ProductInfo;
	private texts : Texts = {};
	private filters? : Filters;
	private initialized = false;
	private rewriteUrlParser? : HTMLAnchorElement;
	private canonicalPathName! : string;

	public on(callback : Hook) {
		this.channel = INITIAL_CHANNEL_NAME + getUniqueIdOfElement(this.$ctx);
		this.segment = ($(document.body).data('page-segment') || '').toLowerCase() === Segment.SME
			? Segment.SME : Segment.RES;
		this.isResponsibleForPage = this.isResponsibleFor($(document.body));
		this.supportsHistoryApi = !this.isEditMode && this.isResponsibleForPage && !!w.history && !!w.history.replaceState;

		const subscriptionId = this.$ctx.data('initial-subscription-id') ?
			this.$ctx.data('initial-subscription-id').split('|') : [];

		const subscriptionType = subscriptionId[0] === SIMPLE_PAY_DUMMY_SUBSCRIPTION ?
			SUBSCRIPTION_TYPES.without : SUBSCRIPTION_TYPES.createNew;

		this.defaults = {
			freetext: '',
			productId: this.$ctx.data('initial-product-id'),
			subscriptionDuration: this.$ctx.data('initial-subscription-duration') || 24,
			subscriptionId,
			subscriptionType,
		};

		this.texts = {};
		([].slice.call(this.$ctx[0].attributes) as Attr[])
			.filter(attr => attr.name.toLowerCase().indexOf('data-txt-') === 0)
			.forEach(attr => {
				this.texts[attr.name.substring('data-txt-'.length).toLowerCase()] = attr.value;
			});

		this.initProductFamily();

		if(this.productFamily.getConfigError()) {
			this.log.error('Product data is incorrectly configured.', this.productFamily.getConfigError());
		}

		if (this.supportsHistoryApi) {
			this.initRewriteUrl(w.location.href);
		}
		if (this.isResponsibleForPage) {
			this.applyDeepLinking();
		}

		this.initStatus();
		this.sandbox.subscribe(INITIAL_CHANNEL_NAME, this);
		if (this.isResponsibleForPage) {
			this.sandbox.subscribe(INTERACTIVE_CONTAINER_CHANNEL_NAME, this);
		}
		this.sandbox.subscribe(this.channel, this);
		this.fire('startProductDataSubscription', [INITIAL_CHANNEL_NAME]);

		callback();
	}

	public after() {
		this.onUpdateStatus({});
		this.initialized = true;
	}

	/**
	 * Initializes the product family
	 *
	 * @method initProductFamily
	 * @return void
	 */
	public initProductFamily() {
		const json : FamilyData = this.$ctx.data('json');
		const variants : VariantData = this.$ctx.data('variants');
		const overrides : Overrides = this.$ctx.data('overrides') || {};
		const featureGroups : FeatureGroups = this.$ctx.data('feature-groups') || {};
		const bestseller : string = this.$ctx.attr('data-bestsellers') || '';
		const warranties : WarrantyInfo = this.$ctx.data('warranties') || {};

		this.productFamily = new Family(
			json,
			variants,
			this.segment,
			featureGroups,
			bestseller,
			this.texts,
			mapVariantsToWarranties(warranties),
		);

		this.productFamily.setOverrides(overrides);

		if(this.debug) {
			(this as any)._json = json;
			(this as any)._variants = variants;
			(this as any)._overrides = overrides;
		}
	}

	/**
	 * Initializes the current status
	 *
	 * @method initStatus
	 * @return void
	 */
	public initStatus() {
		let product = this.productFamily.getProductById(this.defaults.productId!);

		if (!product && this.productFamily.getProducts().length > 0) {
			product = this.productFamily.getProducts()[0];
		}

		this.currentStatus = this.currentStatus || {};

		let subscriptionId = null;
		if(!Array.isArray(this.defaults.subscriptionId)) {
			this.defaults.subscriptionId = [this.defaults.subscriptionId] as string[];
		}

		const subscriptions = product && product.getSubscriptions();
		if(subscriptions) {
			const subscriptionValid = (id : string) => subscriptions.contains(id);

			// Find the first of our default subscriptions, if exists
			subscriptionId = this.defaults.subscriptionId.filter(subscriptionValid)[0] || null;
		}

		// Init filters
		this.getFilters();
		const filters = this.filters!;

		// Fall back on first configured subscription
		if(!subscriptionId && filters.subscriptionId.length) {
			subscriptionId = filters.subscriptionId[0].value;
		}

		let subscriptionDuration = this.defaults.subscriptionDuration;
		// Subscription exists but not with given duration
		if(subscriptionId && subscriptions && !subscriptions.contains(subscriptionId, subscriptionDuration)) {
			subscriptionDuration = subscriptions.getMaxDuration(subscriptionId) || 0;
		}

		this.currentStatus = {
			...(this.currentStatus || {}),
			...{
				color: product ? product.getColor() : null,
				dimension: product && product.getDetails().getDimension() ? product.getDetails().getDimension()!.getValue() : null,
				memory: product ? product.getMemory() : null,
				quantity: 1,
				subscriptionDuration,
				subscriptionId,
				subscriptionType: this.defaults.subscriptionType || null,
			},
		};
	}

	/**
	 * Parse URL for deep linking, overwrite mod.default values
	 * Examples: .../<familyAlias>(<freetext>;<aboID>,<aboDuration>,<productID>).html
	 * .../<familyAlias>(<productID>).html
	 * .../<familyAlias>(<aboID>,<aboDuration>,<productID>).html
	 */
	public applyDeepLinking() {
		const patt = /\((.*)\)(.*).html/;
		let urlContent;
		let arr;
		let str;
		let filters;
		const url = decodeURIComponent(w.location.href);
		const hashParams = window.location.hash.slice(1);
		const parameters = deparam(hashParams);

		this.log('applyDeepLinking > uri', url, patt.test(url), parameters);
		if (!patt.test(url)) {
			return;
		}
		urlContent = patt.exec(url);
		if (!urlContent || urlContent.length < 2) {
			return;
		}
		arr = urlContent[1].split(';');
		str = arr[0];
		if (arr.length > 1) {
			// Found freetext
			this.defaults.freetext = arr[0] + ';';
			str = arr[1];
		}
		const data = str.split(',');

		if (data.length === 1) {
			this.defaults.productId = data[0];
		} else if (data.length === 3) {
			filters = this.getFilters();

			// Check if subscription ID is valid
			if(filters.subscriptionId.find(id => id.value === data[0])) {
				this.defaults.subscriptionId = data[0];
			}

			// Check if subscription duration is valid
			if(filters.subscriptionDuration.find(duration => String(duration.value) === data[1])) {
				this.defaults.subscriptionDuration = data[1];
			}

			this.defaults.productId = data[2];
		}
		// If the user gets redirected from login to the #retention case, preset filter accordingly
		if (typeof parameters.retention !== 'undefined') {
			if (!this.isMobile) {
				this.defaults.subscriptionId = SUBSCRIPTION_TYPES.retention;
			}
			this.defaults.subscriptionType = SUBSCRIPTION_TYPES.retention;
			this.defaults.subscriptionDuration = '';
		}
	}

	// See Tc.Module.RetentionDisplay.js and Tc.Module.RetentionResult.ts
	public onRemoveHashParameter(param : string) {
		if (!this.rewriteUrlParser) {
			return;
		}
		const url = this.rewriteUrlParser.href;
		this.rewriteUrlParser.href = removeHashParameterFromUrl(url, param);
	}

	/**
	 * Initializes data for rewrite url
	 * @param {string} url
	 */
	public initRewriteUrl(url : string) {
		this.rewriteUrlParser = document.createElement('a');

		const canonicalHref = $('link[rel=canonical]').attr('href');
		if (canonicalHref) {
			this.rewriteUrlParser.href = canonicalHref;
			this.canonicalPathName = this.rewriteUrlParser.pathname.split(/.html\/?/)[0];
		}

		this.rewriteUrlParser.href = url;
		const pathnameArr = this.rewriteUrlParser.pathname.split(/.html\/?/);
		this.rewriteUrlParser.suffix = '';
		if (pathnameArr.length > 1 && pathnameArr[1]) {
			this.rewriteUrlParser.suffix = '/' + pathnameArr[1];
		}
		this.rewriteUrlParser.pathNameOnly = pathnameArr[0].split('(')[0];
		let selectorsArr = pathnameArr[0].replace(/\(.*\)/, '').split('.');
		selectorsArr.shift();
		selectorsArr = $.grep(selectorsArr, a => a !== '');
		this.rewriteUrlParser.selectors = '';
		if (selectorsArr.length > 0) {
			this.rewriteUrlParser.selectors = '.' + selectorsArr.join('.');
		}

		if (!this.canonicalPathName) {
			this.canonicalPathName = this.rewriteUrlParser.pathNameOnly;
		}

		// Fix for IE 11
		if (this.canonicalPathName.substring(0, 1) !== '/') {
			this.canonicalPathName = '/' + this.canonicalPathName;
		}
		// Prevent jquery mobile from reloading the page on the popstate event
		$(w).on('popstate', event => event.preventDefault());
	}

	/**
	 * Create deep link for info.product
	 * @param {Object} info
	 * @param {Object} info.product
	 * @returns {string} the deeplink or ''
	 */
	public getDeeplink(info : InfoWithProduct) {
		const product = info.product;

		if (!product) {
			return '';
		}

		const hasSubscription = product.canHaveSubscriptions() &&
			!!this.currentStatus.subscriptionId &&
			!!this.currentStatus.subscriptionDuration;

		let subscriptionInfo = '';
		if(hasSubscription) {
			subscriptionInfo = [
				this.currentStatus.subscriptionId,
				this.currentStatus.subscriptionDuration,
			]
				.filter(Boolean)
				.map(String)
				.map(encodeURIComponent)
				.join(',');
		}

		return `(${this.defaults.freetext}${subscriptionInfo},${product.getId()})`;
	}

	/**
	 * Prepare rewrite URL
	 * @param {string} deeplink
	 * @returns {string} the URL
	 */
	public getRewriteUrl(deeplink : string) {
		// tslint:disable-next-line:prefer-template
		return this.canonicalPathName +
			deeplink +
			this.rewriteUrlParser!.selectors +
			'.html' +
			this.rewriteUrlParser!.suffix +
			this.rewriteUrlParser!.search +
			this.rewriteUrlParser!.hash;
	}

	/**
	 * Rewrite URL in address
	 *
	 * @param {Object} info
	 * @param {Object} info.product
	 * @affects {string} mod.currentStatus.deeplink
	 * @affects {string} mod.currentStatus.url
	 * @affects {string} info.deeplink
	 * @affects {string} info.url
	 */
	public rewriteUrl(info : InfoWithProduct) {
		// Dev mode: disable rewrite if requested
		if(
			(this.debug && document.location.hash.indexOf('disableUrlRewrite') > -1) ||
			$(document.body).is('.cq-wcm-edit') ||
			location.host === 'localhost:4502'
		) {
			this.log('rewriteUrl > disabled');

			return;
		}

		const product = info.product;

		if (!product) {
			return;
		}
		const deeplink = this.getDeeplink(info);
		const url = this.getRewriteUrl(deeplink);

		this.log('rewriteUrl > uri: ', url);

		w.history.replaceState({}, '', url);

		// Update currentStatus
		this.currentStatus = this.currentStatus || {};
		Object.assign(
			this.currentStatus,
			{deeplink, url},
		);
		// Update info
		Object.assign(
			info,
			{deeplink, url},
		);
	}

	/**
	 * Retrieve a product based on the given status. If no status is given, take the currently active one.
	 *
	 * @method getProduct
	 * @param {Object} [status] - The status of the product. May be left undefined.
	 * @return {?Object} The found product or null if none is found.
	 */
	public getProduct(status? : RequestProductInfo) {
		status = status || this.currentStatus;

		return this.productFamily.getProductByFilter(
			status.color || undefined,
			status.memory || undefined,
			status.dimension || undefined,
		);
	}

	/**
	 * Gets the information about a product based on the combination of the given
	 * and currently active status.
	 *
	 * @method getInfo
	 * @param {Object} status - The status for which to get the information for.
	 * @return {Object} The information for the given status
	 */
	public getInfo(requestedStatus : RequestProductInfo = {}) : ExposedProductInfo {
		const status : ProductInfo = {
			...this.currentStatus,
			...requestedStatus,
		};

		const product = this.getProduct(status);

		return {
			...status,
			deepLink: status.deepLink || false,
			family: this.productFamily,
			product : product || undefined,
			url: status.url || false,
		};
	}

	/**
	 * Checks if the hub component is responsible for the given dom element.
	 *
	 * @method isResponsibleFor
	 * @param {jQuery} element - The element to check
	 * @return {Boolean} True if this component is responsible for the element, false otherwise.
	 */
	public isResponsibleFor($element : JQuery) {
		const containerSelector = '.product-data-hub-container, body';
		if ($element) {
			return $element.closest(containerSelector).has(this.$ctx[0]).length > 0 &&
				(
					this.$ctx.closest(containerSelector).has($element[0]).length > 0 ||
					this.$ctx.closest(containerSelector).is($element)
				);
		}

		return false;
	}

	/**
	 * @callback productDataCallback
	 * @param {string} channel - name of the channel
	 * @param {Boolean} initialized - product data already loaded?
	 */

	/**
	 * Channel listener which decides if this hub component is responsible for the calling module and
	 * notifies the callers about which channel they should use to communicate with this component.
	 *
	 * @method onSubscribeForProductData
	 * @param {Object} data
	 * @param {jQuery} data.element - the calling DOM container
	 * @param {productDataCallback} data.subscribe - the callback function
	 * @return void
	 */
	public onSubscribeForProductData(data : {
		element : JQuery;
		subscribe(channel : string, immediately : boolean) : void;
	}) {
		if (this.isResponsibleFor(data.element)) {
			data.subscribe(this.channel, this.initialized);
		}
	}

	/**
	 * Channel listener which gets called when the status has changed.
	 * Notifies other modules which have registered to this component about the data changes.
	 *
	 * @method onUpdateStatus
	 * @param {Object} data
	 * @param {string} data.origin - id of the calling module
	 * @param {Object} data.status - the new status properties
	 * @return {Object} info
	 */
	public onUpdateStatus(data : {
		origin? : string;
		status? : RequestProductInfo;
	}) {

		$.extend(this.currentStatus, data.status);
		let origin = data.origin;

		if (!this.isMobile) {
			if (this.currentStatus.subscriptionId && this.currentStatus.subscriptionId !== SUBSCRIPTION_TYPES.retention) {
				if (this.currentStatus.subscriptionType !== SUBSCRIPTION_TYPES.createNew) {
					this.currentStatus.subscriptionType = SUBSCRIPTION_TYPES.createNew;
				}
			} else if (this.currentStatus.subscriptionId === SUBSCRIPTION_TYPES.retention) {
				this.currentStatus.subscriptionType = SUBSCRIPTION_TYPES.retention;
			} else if (this.currentStatus.subscriptionType === SUBSCRIPTION_TYPES.createNew) {
				let defaultSubscriptionId = this.defaults.subscriptionId || null;
				if(Array.isArray(defaultSubscriptionId)) {
					defaultSubscriptionId = defaultSubscriptionId[0];
				}
				this.currentStatus.subscriptionId = defaultSubscriptionId;
			}
		}

		let info = this.getInfo();
		const propertiesForNextProduct = ['memory', 'dimension'];

		while(!info.product && propertiesForNextProduct.length) {
			// Product does not exist, search for the first one available by resetting memory or dimension sizes to the default
			this.selectNextExistingProductBasedOnProperty(propertiesForNextProduct.shift() as keyof ProductInfo);
			info = this.getInfo();
			origin = getUniqueIdOfElement(this.$ctx);
		}

		if(!info.product) {
			this.log.error('No variant available to fall back on, got:', info, 'requested:', data, this.productFamily.getId());

			return undefined;
		}

		if(info.subscriptionId &&
			!info.product.getSubscriptions().get(info.subscriptionId, info.subscriptionDuration || undefined)) {
			// If the subscription does not exist in the previously selected duration, default to the max duration
			this.currentStatus.subscriptionDuration = info.product.getSubscriptions().getMaxDuration(
				info.subscriptionId || undefined,
			);
			info = this.getInfo();
		}

		if (this.supportsHistoryApi) {
			this.rewriteUrl(info as InfoWithProduct);
		}

		this.fire('invalidateProductData', {
			info,
			origin,
		}, [this.channel]);

		this.updateInteractiveContainers();

		return info;
	}

	/**
	 * Selects the next existing product by changing the given product property.
	 *
	 * @method selectNextExistingProductBasedOnProperty
	 * @param {String} propertyName - Product property name which should be changed
	 * @return void
	 */
	public selectNextExistingProductBasedOnProperty(propertyName : keyof ProductInfo) {
		const filter = this.getFilters()[propertyName];
		const startingValue = this.currentStatus[propertyName];

		let lastValidStatus : RequestProductInfo | null = null;

		for(const f of filter) {
			const status : RequestProductInfo = {};
			status[propertyName] = f.value as any;
			const info = this.getInfo(status);

			if(info.product) {
				lastValidStatus = status;
				if (String(startingValue) === f.value) {
					break;
				}
			}
		}

		if (lastValidStatus) {
			Object.assign(this.currentStatus, lastValidStatus);
		}
	}

	/**
	 * Checks if a specific filter should be disabled based on the availability status
	 * of the product and notifies the calling module.
	 *
	 * @method onGetFilterStates
	 * @return void
	 */
	public onGetFilterStates(data : FilterStateRequest) {
		if (!this.filters || !this.filters[data.filterName]) {
			return;
		}

		const filters = this.filters[data.filterName];

		const awaited = filters.map(filter => filter.value).map(filter => {
			const status = {
				...this.currentStatus,
				[data.filterName]: filter,
			};
			const product = this.getProduct(status);
			const deferred = $.Deferred<AvailabilityForFilter>();
			if(product) {
				const availability = product.getAvailability();
				const resolve = () => {
					// Resolve with data we need once availability info is loaded
					// If this is in a failure callback, isAvailable will simply be false
					deferred.resolve({filter, exists: true, isAvailable: availability.isActionable()});
				};
				// Resolve both in success and error case
				// tslint:disable-next-line:no-floating-promises
				availability.load().done(resolve).fail(resolve);
			} else {
				// Product does not exist for the size requested, resolve immediately
				deferred.resolve({filter, exists: false});
			}

			return deferred.promise();
		});

		// tslint:disable-next-line:no-floating-promises
		$.when.apply($, awaited).done((...infos : AvailabilityForFilter[]) => {
			// Called when we have existance/availability info for all products
			const result : FilterStates = {
				disableValues: [],
				hideValues: [],
			};
			infos.forEach(info => {
				if(!info.exists) {
					result.hideValues.push(info.filter);
				} else if(!info.isAvailable) {
					result.disableValues.push(info.filter);
				}
			});
			try {
				data.callback(result);
			} catch (err) {
				this.log('Probably issue with ecommerce service, json response is empty object', err);
			}
		});
	}

	/**
	 * Returns the subscription durations based on currently selected subscription
	 *
	 * @method onGetSubscriptionDurations
	 * @return void
	 */
	public onGetSubscriptionDurations(data : {
		callback(filters : Filters) : void;
	}) {
		const filters : Filters = {...this.filters};
		const product = this.getProduct();

		const subscriptionDurations = (product && product.getSubscriptions().getDurations(
			this.currentStatus.subscriptionId || undefined,
		) || [])
			.map(duration => {
				return {value: String(duration), text: `${duration} ${this.texts.months}`};
			});

		if (subscriptionDurations.length) {
			filters.subscriptionDuration = subscriptionDurations;
		}

		data.callback(filters);
	}

	/**
	 * @callback filtersCallback
	 * @param {Object} filters - the filters
	 */

	/**
	 * Initializes all filters and notifies calling modules about them.
	 *
	 * @method onGetFilters
	 * @param {Object} data
	 * @param {filtersCallback} data.callback - function to be called to notify the calling module
	 * @return void
	 */
	public onGetFilters(data : {
		callback(filters : Filters) : void;
	}) {
		data.callback(this.getFilters());
	}

	public getFilters() {
		const products = this.productFamily.getProducts();
		const colors = [];
		const memory = [];
		const subscriptionType = [];
		const dimension = [];

		const subscriptionDuration : ValueListItem[] = [];
		const subscriptionTextValueList : SubscriptionValueListItem[] = [];

		if (this.filters) {
			return this.filters;
		}
		for(const product of products) {
			if (product.getColor()) {
				colors.push({value: product.getColor(), text: product.getColorText() || ''});
			}
			if (product.getMemory()) {
				memory.push({value: product.getMemory(), text: product.getMemoryText()});
			}
			if (product.getDetails().getDimension()) {
				dimension.push({
					text: product.getDetails().getDimension()!.getValueFormatted(),
					value: product.getDetails().getDimension()!.getValue(),
				});
			}

			if (!product.getSubscriptions()) {
				continue;
			}

			subscriptionDuration.push(
				...(product.getSubscriptions().getDurations() || [])
					.map((duration : number) : ValueListItem => {
						return {
							text: `${duration} ${this.texts.months}`,
							value: String(duration),
						};
					}),
			);

			subscriptionTextValueList.push(...product.getSubscriptions().getTextValueList());
		}

		subscriptionType.push({
			text: this.texts.newsubscription,
			value: SUBSCRIPTION_TYPES.createNew,
		});
		subscriptionType.push({
			text: this.texts.extendsubscription,
			value: SUBSCRIPTION_TYPES.retention,
		});
		subscriptionType.push({
			text: this.texts.nosubscription,
			value: SUBSCRIPTION_TYPES.without,
		});

		this.filters = {
			color: removeDuplicates(colors),
			dimension: removeDuplicates(dimension),
			memory: removeDuplicates(memory),
			subscriptionDuration: removeDuplicates(subscriptionDuration),
			subscriptionId: removeDuplicates(subscriptionTextValueList.sort(compareSubscriptions)),
			subscriptionType: removeDuplicates(subscriptionType),
		};

		this.filters.memory.sort((a : ValueListItem, b : ValueListItem) => {
			return compareNumbers(Number(a.value), Number(b.value));
		});

		return this.filters;
	}

	/**
	 * Get detail data and notifies calling modules about them.
	 *
	 * @method onGetDetails
	 * @return void
	 */
	public onGetDetails(data : {
		callback(details : Details) : void;
	}) {
		// FIXME NEC: Loading details is no longer an async call
		data.callback(this.getProduct()!.getDetails());
	}

	/**
	 * @callback infoCallback
	 * @param {Object} info - the info
	 */

	/**
	 * Channel listener to notify calling modules about the requested status.
	 *
	 * @method onGetInfo
	 * @param {Object} data
	 * @param {Object} data.status - status for which to get the info
	 * @param {infoCallback} data.callback - function to be called to notify the calling module
	 * @return void
	 */
	public onGetInfo(data : {
		status : RequestProductInfo;
		callback?(info : ProductInfo) : void;
	}) {
		if (data.callback) {
			data.callback(this.getInfo(data.status));
		}
	}

	/**
	 * Notifies possibly listening interactive containers about the changed status values.
	 *
	 * @method updateInteractiveContainers
	 * @return void
	 */
	public updateInteractiveContainers() {
		if (this.isResponsibleForPage) {
			for (const name in this.currentStatus) {
				this.fire('updateInteractiveState', {
					name: PREFIX_FILTER_NAME + name,
					origin: INTERACTIVE_CONTAINER_CHANNEL_NAME,
					value: this.currentStatus[name as keyof ProductInfo],
				}, [INTERACTIVE_CONTAINER_CHANNEL_NAME]);
			}
		}
	}

	/**
	 * Channel listener which gets called when an interactive container gets active.
	 * Checks if a valid status value was changed and updates its status based on it.
	 *
	 * @method onInteractiveStateChanged
	 * @param {Object} data
	 * @param {string} data.name - name of the changed property
	 * @param {string} data.value - value of the changed property
	 * @param {string} [data.origin] - undefined if fired by Tc.Module.BackgroundContainer
	 * @return void
	 */
	public onInteractiveStateChanged(data : {
		name : keyof ProductInfo;
		value : string;
		origin? : string;
	}) {
		if (data.origin !== INTERACTIVE_CONTAINER_CHANNEL_NAME &&
			data.name && data.name.indexOf(PREFIX_FILTER_NAME) === 0 &&
			$.inArray(data.name.substring(PREFIX_FILTER_NAME.length), STATUS_PROPERTIES) >= 0) {

			if (this.currentStatus.subscriptionType === SUBSCRIPTION_TYPES.createNew) {
				const statusKey = data.name.substring(PREFIX_FILTER_NAME.length) as keyof ExposedProductInfo;
				this.onUpdateStatus({
					origin: INTERACTIVE_CONTAINER_CHANNEL_NAME,
					status: {
						[statusKey]: data.value,
					},
				});
			}
		}
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.EcommerceProductDataHub.ts